import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocale } from "../../../i18n/config";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import "./patientListAppointments.css";
import { Modal } from "react-bootstrap";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import { Button } from "../../../components/ui";
import logoImg from "../../../media/icon_refresh.png";
import LogoText from "../../../media/60njktpitzg8gano2erfouonjejsbaz5.png";
import { getGroupTypeLabel } from "../../../i18n/groupTypes";

const normalizeLogValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

function PatientListAppointments() {
  const { t, i18n } = useTranslation("patient");
  const locale = getLocale(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { patientId, planId } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [currentAppointmentNumber, setCurrentAppointmentNumber] =
    useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTechnologyModal, setShowTechnologyModal] = useState(false);
  const [currentTechnology, setCurrentTechnology] = useState(null);

  const [showMarkModal, setShowMarkModal] = useState(false);
  const [appointmentToMark, setAppointmentToMark] = useState(null);

  useEffect(() => {
    fetchPlan();
  }, [patientId, planId]);

  const fetchPlan = async () => {
    const currentPlanId = Number(planId);
    try {
      const data = await GetData({
        module: "patient",
        block: "list_appointments",
        id_patient: patientId,
        id_plan: Number.isFinite(currentPlanId) && currentPlanId > 0 ? currentPlanId : 0,
      });
      setPatient(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Функция для получения максимального номера приема
  const getMaxAppointmentNumber = (groups) => {
    if (!groups || groups.length === 0) return 0;

    let maxNumber = 0;
    groups[0].items.forEach((item) => {
      if (item.appointments) {
        item.appointments.forEach((appointment) => {
          const appointmentNumber = parseInt(
            appointment.appointment_number.value,
          );
          if (appointmentNumber > maxNumber) {
            maxNumber = appointmentNumber;
          }
        });
      }
    });
    return maxNumber;
  };

  // Функция для получения приема по номеру
  const getAppointmentById = (appointments, appointmentNumber) => {
    if (!appointments) return null;
    return appointments.find(
      (apt) => parseInt(apt.appointment_number.value) === appointmentNumber,
    );
  };

  // Функция для проверки, отмечен ли прием
  const isAppointmentMarked = (appointmentNumber) => {
    if (!patient || !patient.result.groups?.[0]) return false;

    const group = patient.result.groups[0];
    for (let item of group.items) {
      const appointment = getAppointmentById(
        item.appointments,
        appointmentNumber,
      );

      if (appointment && appointment.id_admission.value !== "0") {
        const admissionInfo =
          patient.result.admission?.[appointment.id_admission.value];
        const dateValue = admissionInfo?.date?.value;

        // Прием считаем "отмеченным" (кнопка "Инфо"),
        // только если у связанного приема есть валидная дата
        // (отличная от "0000-00-00 00:00" и "0000-00-00 00:00:00")
        if (
          dateValue &&
          dateValue !== "0000-00-00 00:00" &&
          dateValue !== "0000-00-00 00:00:00"
        ) {
          return true;
        }
        // если id_admission есть, но дата невалидна/пустая —
        // продолжаем проверять другие услуги этого приема
      }
    }

    // если ни в одной услуге для этого приема нет валидной даты,
    // считаем прием не отмеченным (кнопка "Отметить")
    return false;
  };

  // Есть ли вообще назначение (строка treatment_plan_appointment) для данного номера приёма
  const hasAppointmentForNumber = (appointmentNumber) => {
    if (!patient || !patient.result.groups?.[0]) return false;

    const group = patient.result.groups[0];
    for (let item of group.items) {
      const appointment = getAppointmentById(
        item.appointments,
        appointmentNumber,
      );
      if (appointment) {
        return true;
      }
    }

    return false;
  };

  // Функция для получения информации о приеме по номеру приема
  const getAdmissionInfoByAppointmentNumber = (appointmentNumber) => {
    if (!patient?.result?.groups?.[0]) return null;

    const group = patient.result.groups[0];
    // Ищем первую услугу с отмеченным приемом для получения admissionId
    for (let item of group.items) {
      const appointment = getAppointmentById(
        item.appointments,
        appointmentNumber,
      );
      if (
        appointment?.id_admission?.value &&
        appointment.id_admission.value !== "0"
      ) {
        return patient?.result?.admission?.[appointment.id_admission.value] || null;
      }
    }
    return null;
  };

  // Форматирование даты в вид dd.mm.YYYY, пустые/нулевые даты отображаем как длинное тире
  const formatDateShort = (dateString) => {
    if (
      !dateString ||
      dateString === "0000-00-00 00:00" ||
      dateString === "0000-00-00 00:00:00"
    ) {
      return "—";
    }

    const [datePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) {
      return "—";
    }

    return `${day}.${month}.${year}`;
  };

  // Получить первую плановую дату приёма,
  // если её нет — первую фактическую дату
  const getFirstPlanOrActualDate = () => {
    const admissions = patient?.result?.admission;
    if (!admissions) return null;
  
    const isValidDate = (dateString) =>
      dateString &&
      dateString !== "0000-00-00 00:00" &&
      dateString !== "0000-00-00 00:00:00" &&
      !isNaN(new Date(dateString).getTime()); // дополнительная проверка на валидность даты
  
    let firstPlanDate = null;
    let firstActualDate = null;
  
    Object.values(admissions).forEach((adm) => {
      const plan = adm?.date_plan?.value;
      const actual = adm?.date?.value;
  
      if (isValidDate(plan)) {
        if (!firstPlanDate || new Date(plan) < new Date(firstPlanDate)) {
          firstPlanDate = plan;
        }
      }
  
      if (isValidDate(actual)) {
        if (!firstActualDate || new Date(actual) < new Date(firstActualDate)) {
          firstActualDate = actual;
        }
      }
    });
  
    // Теперь сравниваем две найденные даты и возвращаем самую раннюю
    if (!firstPlanDate && !firstActualDate) return null;
    if (!firstPlanDate) return firstActualDate;
    if (!firstActualDate) return firstPlanDate;
  
    return new Date(firstPlanDate) < new Date(firstActualDate)
      ? firstPlanDate
      : firstActualDate;
  };

  // Форматирование даты в вид dd.mm (для шапки таблицы)
  const formatDateDayMonth = (dateString) => {
    if (
      !dateString ||
      dateString === "0000-00-00 00:00" ||
      dateString === "0000-00-00 00:00:00"
    ) {
      return "—";
    }

    const [datePart] = dateString.split(" ");
    const [year, month, day] = datePart.split("-");

    if (!year || !month || !day) {
      return "—";
    }

    return `${day}.${month}`;
  };

  // Функция для рендеринга статуса приема по номеру
  const renderAppointmentStatus = (appointments, appointmentNumber) => {
    const appointment = getAppointmentById(appointments, appointmentNumber);
    if (!appointment) {
      // Не назначено на этот приём — ячейка должна быть пустой
      return null;
    }

    // Логика как в isAppointmentMarked:
    // считаем прием "завершённым" (filled) только если у связанного приема
    // есть валидная фактическая дата (отличная от "0000-00-00 00:00" и "0000-00-00 00:00:00")
    if (appointment.id_admission.value !== "0") {
      const admissionInfo =
        patient?.result?.admission?.[appointment.id_admission.value];
      const dateValue = admissionInfo?.date?.value;

      if (
        dateValue &&
        dateValue !== "0000-00-00 00:00" &&
        dateValue !== "0000-00-00 00:00:00"
      ) {
        return "filled";
      }

      // есть id_admission, но фактическая дата не указана/нулевая —
      // отображаем как "запланированный" приём
      return "scheduled";
    }

    // есть назначение в плане (строка treatment_plan_appointment), но ещё без привязки к приёму
    return "scheduled";
  };

  // Обработчик клика на кнопку "Отметить"
  const handleMarkAppointment = (appointmentNumber) => {
    setAppointmentToMark(appointmentNumber);
    setShowMarkModal(true);
  };

  // Обработчик клика на кнопку "Инфо"
  const handleShowInfo = (appointmentNumber) => {
    setCurrentAppointmentNumber(appointmentNumber);
    setModalMode("view");
    setShowModal(true);
  };

  // Обработчик клика по "технология"
  const handleShowTechnology = (technology) => {
    setCurrentTechnology(technology);
    setShowTechnologyModal(true);
  };

  const handlePrint = () => {
    if (typeof window === "undefined") return;

    const body = document.body;
    if (!body) {
      window.print();
      return;
    }

    body.classList.add("print-only-profile");
    window.print();
    body.classList.remove("print-only-profile");
  };

  const handleBackToPlan = () => {
    const currentPlanId = Number(planId);
    if (Number.isFinite(currentPlanId) && currentPlanId > 0) {
      navigate(`/patient/${patientId}/plan/${currentPlanId}`);
      return;
    }
    navigate(`/patient/${patientId}/plan`);
  };

  const handleSaveAdmission = async (data) => {
    const currentPlanId = Number(planId);
    try {
      await GetData({
        module: "patient",
        block: "list_appointments",
        action: "mark_reception",
        id_patient: patientId,
        id_plan: Number.isFinite(currentPlanId) && currentPlanId > 0 ? currentPlanId : 0,
        appointment_number: appointmentToMark,
        pressure: data.pressure,
        comment: data.comment,
        id_user: 123,
      });

      await logPatientChange({
        idPatient: patientId,
        type: "mark_reception",
        text: buildPatientLogText("patient_appointment_marked", {
          details: [
            t("appointments.logAppointment", { number: appointmentToMark }),
            t("appointments.logPressure", { value: normalizeLogValue(data.pressure) }),
            t("appointments.logComment", { value: normalizeLogValue(data.comment) }),
          ].join("; "),
        }),
      });

      // Перезагружаем данные, чтобы обновить таблицу
      await fetchPlan();
    } catch (err) {
      alert(t("appointments.saveFailed", { message: err.message }));
    }
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setShowModal(false);
    setModalMode(null);
    setCurrentAppointmentNumber(null);
  };

  if (loading) return <div className="loading">{t("state.loading", { ns: "common" })}</div>;
  if (error) return <div className="error">{t("error", { ns: "common", message: error })}</div>;
  if (!patient) return <div className="no-data">{t("appointments.dataNotFound")}</div>;

  // Безопасное получение первой группы
  const group = patient?.result?.groups?.[0] || {
    name: { value: "—" },
    items: [],
    id: { value: "" },
  };

  // Безопасный расчёт максимального номера приема
  const maxAppointmentNumber = patient?.result?.groups
    ? getMaxAppointmentNumber(patient.result.groups)
    : 0;

  const appointmentNumbers = Array.from(
    { length: maxAppointmentNumber },
    (_, i) => i + 1,
  );

  console.log(patient.result);

  return (
    <>
      {patient?.result.length === 0 ? (
        <div>{t("appointments.notFound")}</div>
      ) : (
        <div className="patient-list-appointments">
          <div className="patient-name-title-sticky">
            <BlockTitle
              title={`${patient.result.name.value} - ${getGroupTypeLabel(t, group)}`}
              buttons={[
                {
                  label: t("plan.buttons.print"),
                  onClick: handlePrint,
                  type: "light icon",
                  iconOnly: true,
                },
                {
                  label: t("plan.buttons.backToList"),
                  onClick: handleBackToPlan,
                  type: "normal icon",
                  iconOnly: true,
                },
              ]}
            /> 
          </div>

          {/* Информация о пациенте и враче */}
          <div className="patient-info">
            <div className="info-item">
              <span className="info-label">{t("appointments.doctorLabel")}</span>
              <span className="info-value">
                {patient.result.doc_name.value}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label info-label-allergics">
                {t("appointments.allergicsLabel")}
              </span>
              <span className="info-value info-value-allergics">
                {patient.result.allergics?.value == "" ? "—" : patient.result.allergics?.value}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">{t("appointments.cartLabel")}</span>
              <span className="info-value">
                {patient.result.cart_patient.value}
              </span>
            </div>
          </div>

          <div className="patient-info-print">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                gap: 12,
                alignItems: "center",
                marginBottom: 12,
              }}>
              <img
                src={LogoText}
                alt={t("appointments.logoAlt")}
                style={{ width: 140, height: 56, objectFit: "contain" }}
              />
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 18,
                  textTransform: "uppercase",
                  textAlign: "center",
                }}>
                <div>{t("appointments.printTitle")}</div>
              </div>
            </div>
            <div className="info-print-first">
              <div className="info-item-print">
                <div>{t("appointments.printFio", { name: patient.result.fio.value })}</div>
                <div>
                  {t("appointments.printBirthDate")}
                  {formatDateShort(patient.result.birthDate.value)}
                </div>
              </div>
              <div className="info-item-print">
                <div>{t("appointments.printCart", { cart: patient.result.cart_patient.value })}</div>
                <div>
                  {t("appointments.printTreatmentStart")}
                  {formatDateShort(getFirstPlanOrActualDate())}
                </div>
              </div>
              <div className="info-item-print">
                <div>{t("appointments.printDoctor", { name: patient.result.doc_name.value })}</div>
                <div>{t("appointments.printSignature")}</div>
                <div>
                  {t("appointments.printSpecialMarks")}
                </div>
              </div>
            </div>
            <div className="info-print-second">
              <div className="info-item-print-left">
                <div>{t("appointments.printDs")}</div>
                <div>{t("appointments.printAllergyHistory")}</div>
              </div>
              
              <div className="info-print-center">
                  <img src={logoImg} alt={t("appointments.imageAlt")} />
              </div>
            </div>
            
          </div>

          <div className="appointments-group">
            {group.items.length > 0 ? (
              <div className="appointments-table-container">
                <div className="appointments-table">
                  <table>
                    <thead>
                      <tr>
                        <th className="service-name">{t("appointments.colAssignments")}</th>
                        {appointmentNumbers.map((number) => {
                          const admissionInfo =
                            getAdmissionInfoByAppointmentNumber(number);

                          return (
                            <th key={number} className="appointment-header">
                              <div className="appointment-header-title">
                                {t("appointments.colAppointment", { number })}
                              </div>
                              <div className="appointment-header-date">
                                {admissionInfo?.date?.value
                                  ? formatDateDayMonth(admissionInfo.date.value)
                                  : "—"}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                      {/* <tr className="row-set-admission-date">
                        <th className="service-name"></th>
                          {appointmentNumbers.map((number) => {
                            const admissionInfo =
                              getAdmissionInfoByAppointmentNumber(number);
                            return (
                              <th className="appointment-header">
                                <div key={number} className="appointment-header-title">
                                  {admissionInfo?.date_plan?.value
                                    ? formatDateShort(admissionInfo.date_plan.value)
                                    : "—"}
                                </div>
                              </th>
                            );
                          })}

                      </tr> */}
                    </thead>
                    <tbody>
                      {group.items.map((item) => (
                        <tr key={item.id.value} className="service-row">
                          <td className="service-name">
                            <div className="service-title">
                              {item.name.value}
                            </div>
                            <div className="service-composition">
                              {item.composition.value}
                            </div>
                            {(item?.subitems || []).length > 0 && (
                              <div className="service-subitems">
                                {(item.subitems || []).map((sub) => (
                                  <div
                                    key={sub?.id?.value ?? `${item.id.value}-sub-${sub?.name?.value ?? ""}`}
                                    className="service-subitem">
                                    <div className="mb-1">{sub?.name?.value}</div>
                                    <div className="mb-1">{sub?.composition?.value}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div
                              className="service-technology"
                              onClick={() =>
                                handleShowTechnology(item.technology?.value)
                              }>
                              {t("appointments.technology")}
                            </div>
                            <div className="service-technology-full">
                              {item.technology?.value}
                            </div>
                          </td>
                          {appointmentNumbers.map((number) => (
                            <td key={number} className="appointment-cell">
                              {(() => {
                                const status = renderAppointmentStatus(
                                  item.appointments,
                                  number,
                                );
                                if (!status) return null;
                                return (
                                  <span
                                    className={`appointment-indicator ${status}`}
                                  />
                                );
                              })()}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr className="service-row appointment-extra-row">
                        <td className="service-name">{t("appointments.rowAllergics")}</td>
                        {appointmentNumbers.map((number) => (
                          <td key={number} className="appointment-cell" />
                        ))}
                      </tr>

                      <tr className="service-row appointment-extra-row">
                        <td className="service-name">{t("appointments.rowPressure")}</td>
                        {appointmentNumbers.map((number) => {
                          const admissionInfo =
                            getAdmissionInfoByAppointmentNumber(number);
                          const pressure =
                            admissionInfo?.pressure?.value?.trim() || "—";
                          return (
                            <td key={number} className="appointment-cell">
                              <span className="appointment-pressure-value">
                                {pressure}
                              </span>
                            </td>
                          );
                        })}
                      </tr>

                      <tr className="service-row appointment-extra-row">
                        <td className="service-name">{t("appointments.rowNurse")}</td>
                        {appointmentNumbers.map((number) => (
                          <td key={number} className="appointment-cell" />
                        ))}
                      </tr>

                      <tr className="service-row appointment-actions-row">
                        <td className="service-name"></td>
                        {appointmentNumbers.map((number) => {
                          const hasAppointment = hasAppointmentForNumber(number);
                          const marked = hasAppointment
                            ? isAppointmentMarked(number)
                            : false;

                          return (
                            <td key={number} className="appointment-cell">
                              <div
                                key={number}
                                className="appointment-action-cell">
                                {!hasAppointment ? (
                                  // Приём не назначен в плане — кнопок нет
                                  null
                                ) : !marked ? (
                                  <Button
                                    onClick={() =>
                                      handleMarkAppointment(number)
                                    }
                                    size="sm"
                                    variant="secondary">
                                    {t("appointments.mark")}
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={() => handleShowInfo(number)}
                                    size="sm"
                                    variant="success">
                                    {t("appointments.info")}
                                  </Button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="no-services">{t("appointments.noServicesInGroup")}</div>
            )}
          </div>
          {/* Модальное окно для просмотра информации о приёме */}
          <Modal
            show={showTechnologyModal}
            onHide={() => setShowTechnologyModal(false)}
            centered>
            <Modal.Header closeButton>
              <Modal.Title>{t("appointments.technology")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="technology-info">
                {currentTechnology || t("appointments.notSpecified")}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => setShowTechnologyModal(false)}
                variant="primary">
                {t("actions.close", { ns: "common" })}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Модальное окно для просмотра информации о приёме */}
          <Modal show={showModal} onHide={handleCloseModal} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                {t("appointments.infoTitle", { number: currentAppointmentNumber })}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="admission-info">
                {(() => {
                  const admissionInfo = getAdmissionInfoByAppointmentNumber(
                    currentAppointmentNumber,
                  );
                  return (
                    <>
                      <div className="info-row mb-3">
                        <div className="info-label fw-bold">{t("appointments.planDate")}</div>
                        <div className="info-value">
                          {formatDateShort(admissionInfo?.date_plan?.value)}
                        </div>
                      </div>
                      <div className="info-row mb-3">
                        <div className="info-label fw-bold">
                          {t("appointments.actualDate")}
                        </div>
                        <div className="info-value">
                          {admissionInfo?.date?.value
                            ? new Date(admissionInfo.date.value).toLocaleString(
                                locale,
                              )
                            : t("appointments.notSpecified")}
                        </div>
                      </div>
                      <div className="info-row mb-3">
                        <div className="info-label fw-bold">{t("appointments.pressureLabel")}</div>
                        <div className="info-value">
                          {admissionInfo?.pressure?.value || t("appointments.notSpecified")}
                        </div>
                      </div>
                      <div className="info-row">
                        <div className="info-label fw-bold">{t("appointments.commentLabel")}</div>
                        <div className="info-value">
                          {admissionInfo?.comment?.value || t("appointments.noComment")}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={handleCloseModal} variant="primary">
                {t("actions.close", { ns: "common" })}
              </Button>
            </Modal.Footer>
          </Modal>
          {/* Модалка для отметки приёма */}
          <EditableModal
            show={showMarkModal}
            onHide={() => setShowMarkModal(false)}
            title={t("appointments.markTitle", { number: appointmentToMark })}
            fields={[
              {
                name: "pressure",
                label: t("appointments.pressureField"),
                type: "text",
              },
              { name: "comment", label: t("appointments.commentLabel"), type: "textarea" },
            ]}
            initialData={{ pressure: "", comment: "" }}
            onSave={handleSaveAdmission}
          />
        </div>
      )}
    </>
  );
}

export default PatientListAppointments;
